import React from "react";
import { useState } from 'react'
import { Copy, Clock, BookOpen, Code } from 'lucide-react'
import type { Issue } from '../../../shared/types'

interface EnhancedFixDisplayProps {
  issue: Issue
}

const EnhancedFixDisplay: React.FC<EnhancedFixDisplayProps> = ({ issue }) => {
  const [activeTab, setActiveTab] = useState<'quick' | 'detailed' | 'code'>('quick')
  const [copied, setCopied] = useState(false)

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const getDifficultyColor = (difficulty?: string) => {
    switch(difficulty) {
      case 'easy': return 'bg-green-100 text-green-700 border-green-200'
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200'
      case 'hard': return 'bg-red-100 text-red-700 border-red-200'
      default: return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  // Don't render if no fix information is available
  if (!issue.quickFix && !issue.detailedGuide && !issue.fixExample) {
    return null
  }

  return (
    <div className="px-6 pb-4">
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
        {/* Header with difficulty and time */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-green-200">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">How to Fix</span>
            {issue.fixDifficulty && (
              <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getDifficultyColor(issue.fixDifficulty)}`}>
                {issue.fixDifficulty}
              </span>
            )}
            {issue.estimatedTime && (
              <span className="flex items-center gap-1 text-xs text-gray-600">
                <Clock className="w-3 h-3" />
                {issue.estimatedTime}
              </span>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-green-200">
          <button
            onClick={() => setActiveTab('quick')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'quick'
                ? 'bg-white border-b-2 border-green-500 text-green-700'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Quick Fix
          </button>
          {issue.detailedGuide && (
            <button
              onClick={() => setActiveTab('detailed')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'detailed'
                  ? 'bg-white border-b-2 border-green-500 text-green-700'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <span className="flex items-center gap-1">
                <BookOpen className="w-3 h-3" />
                Detailed Guide
              </span>
            </button>
          )}
          {issue.fixExample && (
            <button
              onClick={() => setActiveTab('code')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'code'
                  ? 'bg-white border-b-2 border-green-500 text-green-700'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <span className="flex items-center gap-1">
                <Code className="w-3 h-3" />
                Code Example
              </span>
            </button>
          )}
        </div>

        {/* Tab Content */}
        <div className="p-4">
          {activeTab === 'quick' && issue.quickFix && (
            <div className="text-sm text-gray-700">
              <p className="font-medium text-green-700 mb-2">{issue.quickFix}</p>
              {issue.fixSuggestions && issue.fixSuggestions.length > 0 && (
                <ul className="list-disc list-inside space-y-1 mt-2">
                  {issue.fixSuggestions.map((suggestion, idx) => (
                    <li key={idx} className="text-gray-600">{suggestion}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {activeTab === 'detailed' && issue.detailedGuide && (
            <div className="text-sm text-gray-700 space-y-2">
              <div className="whitespace-pre-line">{issue.detailedGuide}</div>
              {issue.relatedTechniques && issue.relatedTechniques.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-500 mb-1">Related WCAG Techniques:</p>
                  <div className="flex flex-wrap gap-1">
                    {issue.relatedTechniques.map(tech => (
                      <span key={tech} className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'code' && issue.fixExample && (
            <div className="space-y-4">
              {/* Before Code */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-red-700">Before (Incorrect)</span>
                </div>
                <div className="relative bg-red-50 border border-red-200 rounded p-3">
                  <pre className="text-xs text-gray-800 overflow-x-auto">
                    <code>{issue.fixExample.before}</code>
                  </pre>
                </div>
              </div>

              {/* After Code */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-green-700">After (Correct)</span>
                  <button
                    onClick={() => copyToClipboard(issue.fixExample!.after)}
                    className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800"
                  >
                    <Copy className="w-3 h-3" />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="relative bg-green-50 border border-green-200 rounded p-3">
                  <pre className="text-xs text-gray-800 overflow-x-auto">
                    <code>{issue.fixExample.after}</code>
                  </pre>
                </div>
              </div>

              {/* Explanation */}
              {issue.fixExample.explanation && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-xs text-blue-700">{issue.fixExample.explanation}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default EnhancedFixDisplay
